import React, { FC, useState, useRef, useCallback } from 'react';
import Modal from 'components/shared/Modal';
import { ModalRouteProps } from 'context/ModalRouter';
import { copyTextToClipboard } from '@kontentino/kontentino-utils/clipboard';
import Logger from 'utils/logger';
import SHARE from 'images/scenes/share.svg';
import { useQuery } from 'react-query';
import PostApi from 'api/post';
import { queryKey } from 'constants/queryKey';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Icon } from '@kontentino/ui';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { parseQueryParamToNumbers } from 'utils/url';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';

type Props = ModalRouteProps;

const SharePreview: FC<Props> = (props) => {
  const ids = parseQueryParamToNumbers(
    props.modalManager.getQueryParameter('id'),
  );
  const { data, isLoading, error } = useQuery(
    [queryKey.postPublicPreviewUrl, ids],
    () => PostApi.publicPreviewUrl(ids),
  );

  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const onCopyUrl = useCallback(async (url: string) => {
    try {
      await copyTextToClipboard(url);
      setIsCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 3000);
    } catch (e) {
      Logger.error(e);
    }
  }, []);

  const { t } = useTranslation();
  const { hasSome } = useUserPermissions();

  return (
    <Modal
      modalManager={props.modalManager}
      size="responsive"
      classNames={{
        modal: '!tw-w-[480px]',
      }}
    >
      <Modal.Header />
      <Modal.Content>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <img
            src={SHARE}
            alt="Share"
            className="tw-mb-6 tw-h-auto tw-w-full tw-max-w-[233px] "
          />
          <p className="tw-mb-1 tw-text-lg tw-font-semibold">
            {t('sharePreviewWithNotKontentinoUsersTitle', {
              count: ids.length,
            })}
          </p>
          <span
            className="tw-text-center tw-text-md tw-text-grayscale-140"
            data-cy="share-preview_text-content"
          >
            {t('sharePreviewWithNotKontentinoUsersText', {
              count: ids.length,
              validity: hasSome(UserPermission.POST_MASS_EXPORT_MULTIPREVIEW)
                ? 31
                : 7,
            })}
          </span>
        </div>
      </Modal.Content>
      <Modal.Footer>
        {!error && (
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-1">
            <div className="tw-flex tw-w-full tw-items-center tw-gap-2">
              <input
                disabled
                placeholder={
                  isLoading
                    ? `${t('loading', { defaultValue: 'Loading' })}...`
                    : data
                }
                className="tw-w-full tw-max-w-[338px] tw-truncate tw-rounded-md tw-border tw-border-grayscale-30 tw-bg-grayscale-5 tw-px-3 tw-py-[10px] tw-text-md tw-text-grayscale-50"
                data-cy="share-preview_link-data"
              />
              <Button
                onClick={() => data && onCopyUrl(data)}
                variant="primary"
                disabled={isLoading || !data}
                data-cy="share-preview_copy-link-button"
              >
                {t('copyLink')}
              </Button>
            </div>
            <div
              className={`tw-transition-opacity tw-duration-300 ${
                isCopied ? 'tw-opacity-100' : 'tw-opacity-0'
              }`}
            >
              <Field.Caption>
                <span className="tw-flex tw-items-center tw-gap-1 tw-text-success-100">
                  {t('copied', { defaultValue: 'Copied!' })}
                  <Icon icon={faCheck} size="sm" />
                </span>
              </Field.Caption>
            </div>
          </div>
        )}
        {!!error && (
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
            <Alert variant="danger">{t('somethingWentWrongTryAgain')}</Alert>
            <Button
              onClick={props.modalManager.close}
              variant="secondary"
              data-cy="share-preview_copy-close-button"
              className="tw-ml-auto"
            >
              {t('close')}
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SharePreview;
